import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { subDays } from "date-fns";
import { Download, FileText, RefreshCw } from "lucide-react";
import { PageHeader } from "../../components/domain/Bits";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useAuth } from "../../context/AuthContext";
import { canAtLeast, useEffectiveRole } from "../../lib/roles";
import { hasWorkspaceAccess } from "../../lib/workspaces";

const day = (value: Date) => value.toISOString().slice(0, 10);

type ReportData = {
  scope: string;
  filters: { territories: string[] };
  summary: Record<string, number>;
  commission: { paid: number; unpaid: number; non_commissionable: number; currency: string };
  staff_performance: Array<{ user_id: string; name: string; contacts: number; referrals: number; conversions: number; conversion_rate: number }>;
  territory_performance: Array<{ territory: string; referrals: number; conversions: number }>;
  visit_report: { total: number; planned_km: number; completed_km: number; by_status: Array<{ status: string; count: number }> };
  commission_report: { entries: number; by_approval: Array<{ status: string; count: number }> };
  expense_report: { entries: number; by_status: Array<{ status: string; count: number; amount: number }> };
};

const label = (value: string) => value.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Reports() {
  const { user } = useAuth();
  const role = useEffectiveRole();
  const isAdminSupport = role === "admin_support";
  const canViewOperationalReports = canAtLeast(role, "manager_trainer") && hasWorkspaceAccess(user, "reports");
  const canViewReferralReport = hasWorkspaceAccess(user, "referrals") && (
    (isAdminSupport && Boolean(user && user.referral_report_download)) ||
    (!isAdminSupport && canAtLeast(role, "representative"))
  );
  const [fromDate, setFromDate] = useState(day(subDays(new Date(), 29)));
  const [toDate, setToDate] = useState(day(new Date()));
  const [territory, setTerritory] = useState("");
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!canViewOperationalReports) {
      setData(null);
      setError("");
      setLoading(false);
      return;
    }
    setLoading(true); setError("");
    try {
      const response = await api.get<ReportData>("/tenant/reports/summary", {
        params: { from_date: fromDate, to_date: toDate, territory: territory || undefined },
      });
      setData(response.data);
    } catch {
      setError("Could not load reports. Check the date range and try again.");
    } finally { setLoading(false); }
  }, [canViewOperationalReports, fromDate, toDate, territory]);

  useEffect(() => { void load(); }, [load]);

  const download = async (report: "performance" | "visits" | "commissions" | "expenses") => {
    const response = await api.get("/tenant/reports/export", {
      params: { report, from_date: fromDate, to_date: toDate, territory: territory || undefined },
      responseType: "blob",
    });
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `remedikeep-${report}-${fromDate}-to-${toDate}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const metrics = data ? [
    ["Referrals", data.summary.referrals],
    ["Conversions", data.summary.conversions],
    ["Conversion rate", `${data.summary.conversion_rate}%`],
    ["Verified visits", data.summary.visits_verified],
    ["Pending verification", data.summary.visits_pending_verification],
    ["Overdue follow-ups", data.summary.follow_ups_overdue],
  ] : [];

  return <>
    <PageHeader title="Reports & Export Center" subtitle="Role-scoped operational reports and safe exports" />
    <main className="mx-auto max-w-7xl space-y-5 p-6 md:p-8" data-testid="reports-center">
      {canViewReferralReport && (
        <section className="rounded-lg border bg-white p-5" data-testid="referral-status-report-card">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <FileText className="mt-1 h-5 w-5 text-sky-700" />
              <div>
                <h2 className="font-semibold">Referral Status Reports</h2>
                <p className="mt-1 text-sm text-slate-600">Create a doctor-safe PDF grouped by Representative → Clinic → Doctor → Referrals. Your role and record permissions determine what is included.</p>
              </div>
            </div>
            <Button asChild>
              <Link to="/reports/referrals">Open Referral Status Report</Link>
            </Button>
          </div>
        </section>
      )}

      {!canViewOperationalReports && !canViewReferralReport && (
        <div className="rounded-lg border bg-white p-6 text-sm text-slate-600">No reports are available for your current access.</div>
      )}

      {canViewOperationalReports && <section className="grid gap-3 rounded-lg border bg-white p-4 md:grid-cols-[1fr_1fr_1fr_auto]">
        <label className="text-xs font-medium text-slate-600">From
          <Input type="date" className="mt-1" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label className="text-xs font-medium text-slate-600">To
          <Input type="date" className="mt-1" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <label className="text-xs font-medium text-slate-600">Territory
          <select className="mt-1 h-10 w-full rounded-md border px-3 text-sm" value={territory} onChange={(e) => setTerritory(e.target.value)}>
            <option value="">All territories</option>
            {(data?.filters.territories || []).map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <Button variant="outline" className="self-end" disabled={loading} onClick={load}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </section>}

      {canViewOperationalReports && error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {canViewOperationalReports && data && <>
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {metrics.map(([name, value]) => <div key={String(name)} className="rounded-lg border bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">{name}</div>
            <div className="mt-2 text-2xl font-semibold">{value}</div>
          </div>)}
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-lg border bg-white p-5">
            <h2 className="font-semibold">Schedule activity report</h2>
            <p className="mt-1 text-xs text-slate-500">Completed, pending, cancelled, and reimbursed scheduled work</p>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div><div className="text-xs text-slate-500">Visits</div><div className="text-xl font-semibold">{data.visit_report.total}</div></div>
              <div><div className="text-xs text-slate-500">Planned KM</div><div className="text-xl font-semibold">{data.visit_report.planned_km}</div></div>
              <div><div className="text-xs text-slate-500">Actual KM</div><div className="text-xl font-semibold">{data.visit_report.completed_km}</div></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">{data.visit_report.by_status.map((row) =>
              <span key={row.status} className="rounded-full bg-slate-100 px-3 py-1 text-xs">{label(row.status)}: {row.count}</span>)}</div>
            <Button className="mt-5" variant="outline" onClick={() => download("visits")}><Download className="mr-2 h-4 w-4" /> Export schedule activity CSV</Button>
          </div>
          <div className="rounded-lg border bg-white p-5">
            <h2 className="font-semibold">Expense & reimbursement control</h2>
            <p className="mt-1 text-xs text-slate-500">Claim evidence, review, and payment status</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><div className="text-xs text-slate-500">Claims</div><div className="text-xl font-semibold">{data.expense_report.entries}</div></div>
              <div><div className="text-xs text-slate-500">Submitted</div><div className="text-xl font-semibold">{data.expense_report.by_status.find(x => x.status === "submitted")?.count || 0}</div></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">{data.expense_report.by_status.map((row) =>
              <span key={row.status} className="rounded-full bg-slate-100 px-3 py-1 text-xs">{label(row.status)}: {row.count} · {row.amount}</span>)}</div>
            <Button className="mt-5" variant="outline" onClick={() => download("expenses")}><Download className="mr-2 h-4 w-4" /> Export expenses CSV</Button>
          </div>
          <div className="rounded-lg border bg-white p-5">
            <h2 className="font-semibold">Commission control</h2>
            <p className="mt-1 text-xs text-slate-500">Amounts and approval workload for this period</p>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div><div className="text-xs text-slate-500">Unpaid</div><div className="text-xl font-semibold">{data.commission.unpaid} {data.commission.currency}</div></div>
              <div><div className="text-xs text-slate-500">Paid</div><div className="text-xl font-semibold">{data.commission.paid} {data.commission.currency}</div></div>
              <div><div className="text-xs text-slate-500">Entries</div><div className="text-xl font-semibold">{data.commission_report.entries}</div></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">{data.commission_report.by_approval.map((row) =>
              <span key={row.status} className="rounded-full bg-slate-100 px-3 py-1 text-xs">{label(row.status)}: {row.count}</span>)}</div>
            <Button className="mt-5" variant="outline" onClick={() => download("commissions")}><Download className="mr-2 h-4 w-4" /> Export commissions CSV</Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border bg-white">
          <div className="flex items-center justify-between border-b p-4">
            <div><h2 className="font-semibold">Staff performance</h2><p className="text-xs text-slate-500">Referral and conversion output under the active filters</p></div>
            <Button variant="outline" onClick={() => download("performance")}><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
          </div>
          <div className="overflow-x-auto"><table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="p-3">Staff</th><th className="p-3">Contacts</th><th className="p-3">Referrals</th><th className="p-3">Conversions</th><th className="p-3">Rate</th></tr></thead>
            <tbody>{data.staff_performance.map((row) => <tr key={row.user_id} className="border-t">
              <td className="p-3 font-medium">{row.name}</td><td className="p-3">{row.contacts}</td><td className="p-3">{row.referrals}</td><td className="p-3">{row.conversions}</td><td className="p-3">{row.conversion_rate}%</td>
            </tr>)}</tbody>
          </table></div>
          {!data.staff_performance.length && <p className="p-8 text-center text-sm text-slate-500">No activity in this date range.</p>}
        </section>
      </>}
    </main>
  </>;
}
